import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import userProfileService from '../services/userProfile';

export const UserContext = createContext();

// Define task types
export const TASKS = {
    TWITTER_FOLLOW: 'twitter_follow',
    TWITTER_BIO: 'twitter_bio',
    TWITTER_RETWEET: 'twitter_retweet',
    TWITTER_TWEET: 'twitter_tweet',
    DISCORD_JOIN: 'discord_join',
    DISCORD_VERIFY: 'discord_verify',
    DISCORD_REACT: 'discord_react',
    DISCORD_LEVEL: 'discord_level'
};

// Mock data for development
const createMockProfile = (walletAddress) => ({
    walletAddress,
    nickname: '',
    rank: 1,
    totalContribution: 0,
    socials: {
        twitter: {
            connected: false,
            handle: null,
            tasks: {
                TWITTER_FOLLOW: false,
                TWITTER_BIO: false,
                TWITTER_RETWEET: false,
                TWITTER_TWEET: false
            },
            completedTasks: []
        },
        discord: {
            connected: false,
            handle: null,
            tasks: {
                DISCORD_JOIN: false,
                DISCORD_VERIFY: false,
                DISCORD_REACT: false,
                DISCORD_LEVEL: false
            },
            completedTasks: []
        }
    },
    referralCode: `REF${walletAddress.slice(0, 8)}`,
    referrals: [],
    createdAt: new Date().toISOString()
});

// Hook for using the user context
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const { publicKey } = useWallet();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isConnectingTwitter, setIsConnectingTwitter] = useState(false);
    const [isConnectingDiscord, setIsConnectingDiscord] = useState(false);

    // Load user profile when wallet connects
    useEffect(() => {
        const loadUserProfile = async () => {
            if (!publicKey) {
                setUserProfile(null);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                let profile;

                // Try to load from local storage first
                const savedProfile = localStorage.getItem('userProfile');
                if (savedProfile) {
                    profile = JSON.parse(savedProfile);
                    // Verify if the saved profile matches the current wallet
                    if (profile.walletAddress !== publicKey.toString()) {
                        profile = null;
                    }
                }

                if (!profile) {
                    try {
                        // Try to load from backend
                        profile = await userProfileService.getUserProfile(publicKey.toString());
                    } catch (err) {
                        // If backend fails, use mock data
                        console.log('Using mock profile data for development');
                        profile = createMockProfile(publicKey.toString());
                    }
                }

                setUserProfile(profile);
                localStorage.setItem('userProfile', JSON.stringify(profile));
                setError(null);
            } catch (err) {
                console.error('Failed to load user profile:', err);
                setError('Failed to load user profile');
            } finally {
                setLoading(false);
            }
        };

        loadUserProfile();
    }, [publicKey]);

    useEffect(() => {
        // Save user profile to local storage whenever it changes
        if (userProfile) {
            localStorage.setItem('userProfile', JSON.stringify(userProfile));
        }
    }, [userProfile]);

    // Connect Twitter
    const connectTwitter = async () => {
        setIsConnectingTwitter(true);
        try {
            const isConnected = userProfile?.socials?.twitter?.connected;
            
            if (isConnected) {
                // Disconnect Twitter
                const updatedProfile = {
                    ...userProfile,
                    socials: {
                        ...userProfile.socials,
                        twitter: {
                            connected: false,
                            handle: null,
                            tasks: {
                                TWITTER_FOLLOW: false,
                                TWITTER_BIO: false,
                                TWITTER_RETWEET: false,
                                TWITTER_TWEET: false
                            }
                        }
                    }
                };
                setUserProfile(updatedProfile);
                localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
                return updatedProfile;
            }

            // Connect Twitter via OAuth
            const clientId = process.env.REACT_APP_TWITTER_CLIENT_ID;
            console.log('Twitter Client ID:', clientId);
            
            if (!clientId) {
                throw new Error('Twitter client ID not configured. Please check your .env file');
            }

            // Generate code verifier and challenge
            const generateCodeVerifier = () => {
                const array = new Uint8Array(32);
                window.crypto.getRandomValues(array);
                const randomString = Array.from(array, byte => 
                    ('0' + byte.toString(16)).slice(-2)
                ).join('');
                return base64URLEncode(randomString);
            };

            const base64URLEncode = (str) => {
                return btoa(str)
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, '');
            };

            const sha256 = async (plain) => {
                const encoder = new TextEncoder();
                const data = encoder.encode(plain);
                const hash = await window.crypto.subtle.digest('SHA-256', data);
                return base64URLEncode(String.fromCharCode(...new Uint8Array(hash)));
            };

            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await sha256(codeVerifier);

            // Store code verifier for later use
            localStorage.setItem('twitter_code_verifier', codeVerifier);

            const redirectUri = `${window.location.origin}/twitter-callback`;
            const scope = 'users.read tweet.read offline.access';
            const state = base64URLEncode(window.crypto.getRandomValues(new Uint8Array(32)));
            
            // Store state in localStorage to verify when we return
            localStorage.setItem('twitter_oauth_state', state);
            
            // The correct Twitter OAuth 2.0 authorization URL
            const oauthUrl = new URL('https://twitter.com/i/oauth2/authorize');
            oauthUrl.searchParams.append('response_type', 'code');
            oauthUrl.searchParams.append('client_id', clientId);
            oauthUrl.searchParams.append('redirect_uri', redirectUri);
            oauthUrl.searchParams.append('scope', scope);
            oauthUrl.searchParams.append('state', state);
            oauthUrl.searchParams.append('code_challenge', codeChallenge);
            oauthUrl.searchParams.append('code_challenge_method', 'S256');

            console.log('OAuth URL:', oauthUrl.toString());
            
            // Redirect to Twitter OAuth
            window.location.href = oauthUrl.toString();
            
        } catch (error) {
            console.error('Failed to connect Twitter:', error);
            throw new Error(error.message || 'Failed to connect Twitter account');
        } finally {
            setIsConnectingTwitter(false);
        }
    };

    // Connect Discord
    const connectDiscord = async () => {
        setIsConnectingDiscord(true);
        try {
            const isConnected = userProfile?.socials?.discord?.connected;
            
            if (isConnected) {
                // Disconnect Discord
                const updatedProfile = {
                    ...userProfile,
                    socials: {
                        ...userProfile.socials,
                        discord: {
                            connected: false,
                            handle: null,
                            tasks: {
                                DISCORD_JOIN: false,
                                DISCORD_VERIFY: false,
                                DISCORD_REACT: false,
                                DISCORD_LEVEL: false
                            }
                        }
                    }
                };
                setUserProfile(updatedProfile);
                localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
                return updatedProfile;
            } else {
                // Simulate Discord connection
                await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
                const updatedProfile = {
                    ...userProfile,
                    socials: {
                        ...userProfile.socials,
                        discord: {
                            connected: true,
                            handle: 'user#' + Math.floor(1000 + Math.random() * 9000),
                            tasks: {
                                DISCORD_JOIN: false,
                                DISCORD_VERIFY: false,
                                DISCORD_REACT: false,
                                DISCORD_LEVEL: false
                            }
                        }
                    }
                };
                setUserProfile(updatedProfile);
                localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
                return updatedProfile;
            }
        } catch (error) {
            console.error('Failed to connect Discord:', error);
            throw new Error('Failed to connect Discord account');
        } finally {
            setIsConnectingDiscord(false);
        }
    };

    // Complete a task
    const completeTask = (platform, taskId) => {
        setUserProfile(prev => {
            if (!prev?.socials?.[platform]?.connected) {
                return prev;
            }

            const completedTasks = prev.socials[platform].completedTasks || [];
            if (completedTasks.includes(taskId)) {
                return prev;
            }

            return {
                ...prev,
                socials: {
                    ...prev.socials,
                    [platform]: {
                        ...prev.socials[platform],
                        completedTasks: [...completedTasks, taskId]
                    }
                }
            };
        });
    };

    // Check if a specific task is completed
    const isTaskCompleted = (platform, taskId) => {
        return userProfile?.socials?.[platform]?.completedTasks?.includes(taskId) || false;
    };

    const value = {
        userProfile,
        setUserProfile,
        connectTwitter,
        connectDiscord,
        isConnectingTwitter,
        isConnectingDiscord,
        completeTask,
        isTaskCompleted,
        TASKS
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export default UserContext;
